import { defineStore } from "pinia";

export const MODAL_REPORT_QUESTION = "reportQuestionModal";
export const MODAL_ATTACH_ANSWER = "attachAnswerModal";
export const MODAL_DELETE_ANSWER = "deleteAnswerModal";
export const MODAL_EDIT_ANSWER = "editAnswerModal";
export const MODAL_REPORT_ANSWER = "reportAnswerModal";

export const useQuestionStore = defineStore("question", {
  state: () => ({
    question: null,
    isLoaded: false,
    isLoadingQuestion: false,
    openedModal: null,
    [MODAL_REPORT_QUESTION]: {
      input: {
        question: null,
      },
    },
    [MODAL_ATTACH_ANSWER]: {
      output: {
        answer: null,
      },
    },
    [MODAL_DELETE_ANSWER]: {
      input: {
        answer: null,
      },
    },
    [MODAL_EDIT_ANSWER]: {
      input: {
        answer: null,
      },
    },
    [MODAL_REPORT_ANSWER]: {
      input: {
        answer: null,
      },
    },
  }),
  getters: {
    questionAnswers: (state) => {
      return state.question?.answers || [];
    },
    isModalOpen: (state) => (modalId) => state.openedModal === modalId,
    modalOutput: (state) => (modalId) => state[modalId]?.output ?? {},
    modalInput: (state) => (modalId) => state[modalId]?.input ?? {},
  },
  actions: {
    patchAnswer(patch) {
      this.question.answers = this.question.answers.map((answer) => {
        if (answer.id === patch.id) {
          return {
            ...answer,
            ...patch,
          };
        }
        return answer;
      });
    },
    openModal(modalId, inputValue = {}) {
      this.openedModal = modalId;
      this[modalId].input = inputValue;
    },
    closeModal(outputValue = {}) {
      if (!this.openedModal) {
        throw new Error("No Modal to close.");
      }
      this[this.openedModal].output = outputValue;
      this.openedModal = null;
    },
    async reloadQuestion() {
      if (!this.question) {
        throw new Error("No Question to reload.");
      }

      this.isLoaded = false;
      await this.loadQuestion(this.question.seoName);
    },
    async loadQuestion(seoName) {
      if (this.isLoaded && this.question.seoName === seoName) {
        return this.question;
      }

      try {
        this.isLoaded = false;
        this.isLoadingQuestion = true;

        const QUESTION_BY_SEO_NAME = await import(
          "./queries/questionBySeoname.graphql"
        );
        const result = await this.$nuxt.$query(QUESTION_BY_SEO_NAME, {
          seoName,
        });

        this.question = result.questionBySeoName;
        this.isLoaded = true;
        return this.question;
      } catch (err) {
        this.question = null;
      } finally {
        this.isLoadingQuestion = false;
      }
    },
    async reportQuestion(questionId, message) {
      const REPORT_QUESTION = await import(
        "./mutations/reportQuestion.graphql"
      );
      const result = await this.$nuxt.$mutate(REPORT_QUESTION, {
        questionId,
        message,
      });
      return result.reportQuestion;
    },
    async reportAnswer(answerId, message) {
      const REPORT_ANSWER = await import("./mutations/reportAnswer.graphql");
      const result = await this.$nuxt.$mutate(REPORT_ANSWER, {
        answerId,
        message,
      });
      return result.reportAnswer;
    },
    async updateAnswer(answerId, body) {
      const UPDATE_ANSWER = await import("./mutations/updateAnswer.graphql");
      const result = await this.$nuxt.$mutate(UPDATE_ANSWER, {
        answerId,
        body,
      });
      return result.updateAnswer;
    },
    async deleteAnswer(answerId) {
      const DELETE_ANSWER = await import("./mutations/deleteAnswer.graphql");
      const result = await this.$nuxt.$mutate(DELETE_ANSWER, { answerId });
      return result.deleteAnswer;
    },
    /**
     * @deprecated use ~/composables/question/useUpvoteAnswer.js instead
     */
    async upvoteAnswer(answerId) {
      const UPVOTE_ANSWER = await import("./mutations/upvoteAnswer.graphql");
      const result = await this.$nuxt.$mutate(UPVOTE_ANSWER, { answerId });
      if (result.answer) {
        this.patchAnswer(result.answer);
      }
    },
    /**
     * @deprecated use ~/composables/question/useUpvoteAnswer.js instead
     */
    async downvoteAnswer(answerId) {
      const DOWNVOTE_ANSWER = await import(
        "./mutations/downvoteAnswer.graphql"
      );
      const result = await this.$nuxt.$mutate(DOWNVOTE_ANSWER, { answerId });
      if (result.answer) {
        this.patchAnswer(result.answer);
      }
    },
    async subscribeToQuestion(questionId) {
      const SUBSCRIBE_TO_QUESTION = await import(
        "./mutations/subscribeToQuestion.graphql"
      );
      const result = await this.$nuxt.$mutate(SUBSCRIBE_TO_QUESTION, {
        questionId,
      });
      return result.subscribeToQuestion;
    },
    async unsubscribeFromQuestion(questionId) {
      const UNSUBSCRIBE_FROM_QUESTION = await import(
        "./mutations/unSubscribeToQuestion.graphql"
      );
      const result = await this.$nuxt.$mutate(UNSUBSCRIBE_FROM_QUESTION, {
        questionId,
      });
      return result.unSubscribeToQuestion;
    },
    clearAttachedAnswer() {
      this.attachAnswerModal.output.answer = null;
    },
  },
});
